<template>
  <div class="path-form">
    <section class="path-form__section">
      <div class="st-eyebrow">Requirements</div>
      <div class="path-form__requirements">
        <StField group label="Required keywords" hint="A unit must have all of these keywords">
          <KeywordsInput
            :model-value="modelValue.requirements.keywords"
            raw
            uppercase
            suggestion-scope="unit"
            placeholder="e.g. HERO"
            @update:model-value="patchRequirements({ keywords: $event })"
          />
        </StField>
        <StField group label="Excluded keywords" hint="A unit cannot have any of these keywords">
          <KeywordsInput
            :model-value="modelValue.requirements.nonKeywords"
            raw
            uppercase
            suggestion-scope="unit"
            placeholder="e.g. MONSTER"
            @update:model-value="patchRequirements({ nonKeywords: $event })"
          />
        </StField>
      </div>
    </section>

    <section v-for="rank in PathRanks" :key="rank" class="path-form__section">
      <div class="path-form__rank-head">
        <div>
          <div class="st-eyebrow">{{ rank }}</div>
          <p class="path-form__hint">
            {{ rankHint(rank) }}
          </p>
        </div>
      </div>

      <div class="path-form__options">
        <section
          v-for="(option, index) in rankOptions(rank)"
          :key="index"
          class="path-form__option"
        >
          <div class="st-eyebrow">Ability {{ index + 1 }}</div>
          <AbilityForm
            :model-value="option.abilities"
            single
            @update:model-value="patchOption(rank, index, { abilities: $event })"
          />
        </section>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { PathRanks, makeAbility, type IPath, type Option, type OptionSet } from '@scrolldex/shared';
import AbilityForm from '../AbilityForm.vue';
import KeywordsInput from '../KeywordsInput.vue';
import { StField } from '../ui/index.js';

const props = defineProps<{ modelValue: IPath }>();
const emit = defineEmits<{ (e: 'update:modelValue', value: IPath): void }>();

type RankKey = 'aspiring' | 'elite' | 'mighty' | 'legendary';

function rankKey(rank: string): RankKey {
  return rank.toLowerCase() as RankKey;
}

function rankSet(rank: string): OptionSet {
  return props.modelValue[rankKey(rank)];
}

function rankOptions(rank: string): Option[] {
  return twoOptions(rankSet(rank).options);
}

function rankHint(rank: string): string {
  const count = rankOptions(rank).length;
  return count > 1
    ? `When a unit reaches ${rank}, choose one of these options.`
    : `When a unit reaches ${rank}, it gains this option.`;
}

function patch(part: Partial<IPath>) {
  emit('update:modelValue', { ...props.modelValue, ...part });
}

function patchRequirements(part: Partial<IPath['requirements']>) {
  patch({ requirements: { ...props.modelValue.requirements, ...part } });
}

function newOption(): Option {
  return {
    name: '',
    weapons: [],
    abilities: [makeAbility('')],
    keywords: [],
    points: 0,
  };
}

function twoOptions(options: Option[]): Option[] {
  return [options[0] ?? newOption(), options[1] ?? newOption()];
}

function patchRank(rank: string, options: Option[]) {
  const key = rankKey(rank);
  patch({
    [key]: {
      ...props.modelValue[key],
      name: rank,
      options,
    },
  } as Partial<IPath>);
}

function patchOption(rank: string, index: number, part: Partial<Option>) {
  patchRank(
    rank,
    rankOptions(rank).map((option, i) => {
      if (i !== index) return option;
      const next = { ...option, ...part };
      return { ...next, name: next.abilities[0]?.name ?? '' };
    }),
  );
}
</script>

<style scoped>
.path-form {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-4);
}
.path-form__section {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-2);
}
.path-form__requirements {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--st-space-3);
}
.path-form__rank-head {
  display: flex;
  justify-content: space-between;
  gap: var(--st-space-3);
}
.path-form__hint {
  margin: 0;
  color: var(--st-text-muted);
  font-size: var(--st-text-sm);
}
.path-form__option {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-3);
  padding: var(--st-space-3);
  background: var(--st-surface-sunken);
  border: 1px solid var(--st-border);
  border-radius: var(--st-radius-md);
}
.path-form__options {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-3);
}

@media (max-width: 780px) {
  .path-form__requirements {
    grid-template-columns: 1fr;
  }
}
</style>
